// src/components/principal/PhotosToReview.jsx (v8 – minimal class hooks for nicer video review)

import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { API_PATHS } from "../../config";
import { getMediaUrls } from "../../utils/media";
import "../../styles/PhotosToReview.css";

/**
 * Panel for principals to review and approve/reject pending uploads.
 */
const PhotosToReview = ({ onError, isInstagramConnected }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);

  const [igQuota, setIgQuota] = useState(null);
  const [schoolHasWebsite, setSchoolHasWebsite] = useState(true);

  const [rejecting, setRejecting] = useState(null); // { photo, reason }

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const [pendingRes, statusRes] = await Promise.all([
          api.get(API_PATHS.PHOTOS_TO_REVIEW),
          api.get(API_PATHS.REVIEW_STATUS),
        ]);

        if (!isMounted) return;

        const pending = Array.isArray(pendingRes.data) ? pendingRes.data : [];
        setItems(pending);

        const status = statusRes.data || {};
        setIgQuota(
          typeof status.instagram_quota === "number" ? status.instagram_quota : null
        );

        setSchoolHasWebsite(
          typeof status.school_has_website === "boolean"
            ? status.school_has_website
            : true
        );
      } catch (err) {
        console.error("Failed to load photos to review:", err);
        onError?.("Could not load photos to review.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [onError]);

  const handleApprove = async (photo, { sendToWebsite, sendToInstagram }) => {
    setBusy((prev) => ({ ...prev, [photo.id]: true }));
    try {
      await api.post(`/photos/${photo.id}/approve`, {
        send_to_website: sendToWebsite,
        send_to_instagram: sendToInstagram,
      });

      setItems((prev) => prev.filter((p) => p.id !== photo.id));

      if (sendToInstagram && typeof igQuota === "number") {
        setIgQuota((q) => Math.max(q - 1, 0));
      }
    } catch (err) {
      console.error("Approve failed:", err);
      onError?.("Failed to approve this item.");
    } finally {
      setBusy((prev) => ({ ...prev, [photo.id]: false }));
    }
  };

  const openRejectModal = (photo) => {
    setRejecting({ photo, reason: "" });
  };

  const handleConfirmReject = async () => {
    if (!rejecting) return;
    const { photo, reason } = rejecting;

    setBusy((prev) => ({ ...prev, [photo.id]: true }));
    try {
      await api.post(`/photos/${photo.id}/reject`, {
        reason: reason.trim() || null,
      });

      setItems((prev) => prev.filter((p) => p.id !== photo.id));
      setRejecting(null);
    } catch (err) {
      console.error("Reject failed:", err);
      onError?.("Failed to reject this item.");
    } finally {
      setBusy((prev) => ({ ...prev, [photo.id]: false }));
    }
  };

  const handleCancelReject = () => setRejecting(null);

  return (
    <div className="gg-photos-to-review">
      <div className="gg-photos-to-review-header">
        <h2>Photos &amp; Videos to Review</h2>

        {isInstagramConnected && (
          <div className="gg-ig-quota-chip">
            Instagram:{" "}
            {typeof igQuota === "number" ? `${igQuota} posts left today` : "connected"}
          </div>
        )}
      </div>

      {loading && <p>Loading photos…</p>}

      {!loading && items.length === 0 && (
        <p>No photos or videos waiting for review right now.</p>
      )}

      {!loading &&
        items.map((photo) => (
          <ReviewCard
            key={photo.id}
            photo={photo}
            busy={!!busy[photo.id]}
            igConnected={isInstagramConnected}
            igQuota={igQuota}
            schoolHasWebsite={schoolHasWebsite}
            onApprove={handleApprove}
            onReject={openRejectModal}
          />
        ))}

      {rejecting && (
        <div className="gg-modal-backdrop">
          <div className="gg-modal">
            <h3>Reject this upload?</h3>
            <p>
              You can optionally add a short note explaining why. This will be emailed
              to the uploader.
            </p>
            <textarea
              rows={4}
              value={rejecting.reason}
              onChange={(e) =>
                setRejecting((prev) => ({ ...prev, reason: e.target.value }))
              }
              className="gg-modal-textarea"
              placeholder="e.g. Child not in uniform, please re-take tomorrow."
            />
            <div className="gg-modal-actions">
              <button
                type="button"
                className="gg-btn gg-btn-secondary"
                onClick={handleCancelReject}
                disabled={busy[rejecting.photo.id]}
              >
                Cancel
              </button>
              <button
                type="button"
                className="gg-btn gg-btn-danger"
                onClick={handleConfirmReject}
                disabled={busy[rejecting.photo.id]}
              >
                {busy[rejecting.photo.id] ? "Rejecting…" : "Send & Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Individual review card
// -----------------------------------------------------------------------------
const ReviewCard = ({
  photo,
  busy,
  igConnected,
  igQuota,
  schoolHasWebsite,
  onApprove,
  onReject,
}) => {
  const { isVideo, imageUrl, videoUrl, posterUrl } = getMediaUrls(photo);

  const durationSeconds =
    photo.video_duration_ms > 0 ? Math.round(photo.video_duration_ms / 1000) : null;

  const defaultWebsite = schoolHasWebsite;
  const defaultInstagram = !isVideo && igConnected && (igQuota == null || igQuota > 0);

  const [sendToWebsite, setSendToWebsite] = useState(defaultWebsite);
  const [sendToInstagram, setSendToInstagram] = useState(defaultInstagram);

  const canUseInstagram = !isVideo && igConnected && (igQuota == null || igQuota > 0);

  useEffect(() => {
    if (!canUseInstagram) setSendToInstagram(false);
  }, [canUseInstagram]);

  const destinations = ["Parents"]
    .concat(sendToWebsite ? ["School Website"] : [])
    .concat(!isVideo && sendToInstagram ? ["Instagram"] : [])
    .join(", ");

  const cardClass = `gg-review-card ${isVideo ? "gg-review-card--video" : ""}`;
  const thumbClass = `gg-review-thumb ${isVideo ? "gg-review-thumb--video" : ""}`;

  return (
    <div className={cardClass}>
      <div className="gg-review-main">
        <div className={thumbClass}>
          {isVideo ? (
            videoUrl ? (
              <div className="gg-review-video-shell">
                <video
                  src={videoUrl}
                  poster={posterUrl || undefined}
                  className="gg-review-video"
                  muted
                  playsInline
                  controls
                  preload="metadata"
                />
                <div className="gg-review-video-pill">
                  <span>▶ Video</span>
                  {durationSeconds !== null && (
                    <span className="gg-review-video-duration">{durationSeconds}s</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="gg-review-missing-media">Video file not available.</div>
            )
          ) : imageUrl ? (
            <img src={imageUrl} alt={photo.story || "Photo to review"} />
          ) : (
            <div className="gg-review-missing-media">Image not available.</div>
          )}
        </div>

        <div className="gg-review-body">
          {photo.story && <p className="gg-review-story">{photo.story}</p>}

          {photo.uploaded_by_name && (
            <p className="gg-review-meta">
              <small>Uploaded by {photo.uploaded_by_name}</small>
            </p>
          )}

          <div className="gg-destinations-row">
            <span className="gg-destinations-label">Send to:</span>

            <span className="gg-pill gg-pill-locked">Parents</span>

            <button
              type="button"
              className={`gg-pill ${sendToWebsite ? "gg-pill-on" : "gg-pill-off"}`}
              onClick={() => setSendToWebsite((v) => !v)}
              disabled={busy}
            >
              Website {sendToWebsite ? "✓" : ""}
            </button>

            {igConnected && !isVideo && (
              <button
                type="button"
                className={`gg-pill ${
                  sendToInstagram ? "gg-pill-on" : "gg-pill-off"
                } ${!canUseInstagram ? "gg-pill-disabled" : ""}`}
                onClick={() => canUseInstagram && setSendToInstagram((v) => !v)}
                disabled={busy || !canUseInstagram}
              >
                Instagram
                {canUseInstagram ? (sendToInstagram ? " ✓" : "") : " (no quota)"}
              </button>
            )}
          </div>

          <div className="gg-destinations-summary">
            <small>Will send to: {destinations}</small>
          </div>

          <div className="gg-card-actions">
            <button
              type="button"
              className="gg-btn gg-btn-secondary"
              onClick={() => onReject(photo)}
              disabled={busy}
            >
              Reject
            </button>
            <button
              type="button"
              className="gg-btn gg-btn-primary"
              onClick={() => onApprove(photo, { sendToWebsite, sendToInstagram })}
              disabled={busy}
            >
              {busy ? "Saving…" : "Approve for Parents"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotosToReview;
